/**
 * The active dataset — production or synthetic.
 *
 * The counterpart to the backend's `_resolve_dataset`. The server treats the
 * dataset as ambient request context carried on the `X-Data-Hub-Dataset`
 * header, so the client needs somewhere to hold it that is not a prop threaded
 * through every component. This module is that place.
 *
 * Deliberately a plain module-level store rather than React context:
 * the request thunks (`Store/Reducers/DataHubConsoleSlice.ts`) cannot read
 * context, but they are exactly where the header has to be attached.
 * Components subscribe via
 * `useActiveDataset`; the API layer reads `getActiveDataset()` directly.
 *
 * The choice survives a reload — someone who was working on synthetic data and
 * refreshes the page should not silently land back on production and wonder why
 * the counts changed.
 */

export const DATASET_KINDS = ["production", "synthetic"] as const;

export type DatasetKind = (typeof DATASET_KINDS)[number];

export const DEFAULT_DATASET: DatasetKind = "production";

/** Header the backend reads. Must match `routers/datahub.py::DATASET_HEADER`. */
export const DATASET_HEADER = "X-Data-Hub-Dataset";

const STORAGE_KEY = "dataHub.activeDataset";

export const DATASET_LABELS: Record<DatasetKind, string> = {
  production: "Production",
  synthetic: "Synthetic",
};

/** Shown under the selector, so the guarantee is stated where the choice is made. */
export const DATASET_NOTICE =
  "Every pipeline stage and view follows the selected dataset. " +
  "The two datasets are never co-mingled.";

export function isDatasetKind(value: unknown): value is DatasetKind {
  return DATASET_KINDS.includes(value as DatasetKind);
}

/** Anything unrecognised resolves to production, mirroring the server. */
export function coerceDataset(value: unknown): DatasetKind {
  if (typeof value !== "string") return DEFAULT_DATASET;
  const candidate = value.trim().toLowerCase();
  return isDatasetKind(candidate) ? candidate : DEFAULT_DATASET;
}

function readStored(): DatasetKind {
  try {
    return coerceDataset(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    // Private browsing, disabled storage, SSR — production is always safe.
    return DEFAULT_DATASET;
  }
}

let active: DatasetKind = readStored();

type Listener = (dataset: DatasetKind) => void;
const listeners = new Set<Listener>();

export function getActiveDataset(): DatasetKind {
  return active;
}

export function setActiveDataset(next: unknown): DatasetKind {
  const resolved = coerceDataset(next);
  if (resolved === active) return active;
  active = resolved;
  try {
    window.localStorage.setItem(STORAGE_KEY, resolved);
  } catch {
    // Not being able to remember the choice is not a reason to refuse it.
  }
  listeners.forEach(fn => fn(resolved));
  return resolved;
}

export function subscribeToDataset(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/**
 * Request options carrying the active dataset.
 *
 * Every Data Hub call goes through this, so there is one place that decides
 * what the server is told — and no call site that can forget.
 */
export function datasetHeaders(): { headers: Record<string, string> } {
  return { headers: { [DATASET_HEADER]: active } };
}
